import { Box, Grid, IconButton } from "@material-ui/core";
import MaterialTable from "material-table";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createFloor, deleteFloorById, getAllFloor, updateFloorById } from "../../../../../api/buildingSettingApi";
import { swalCondition } from "../../../../../utils/swalHelper";
import { floorTableColumns, initialFloorTableOptions, initialFloorValues } from "../helpers/AssignBuildEvtOption";
import { Modal, ModalBody } from "react-bootstrap";
import FormFloor from "./FormFloor";
import { Close } from "@material-ui/icons";
import { useSelector } from "react-redux";

export default function AssignBuildFloor({ buildingOption = [] }) {
    // ref
    const controllerRef = useRef(null);
    const tableRef = useRef(null);

    // global redux state
    const { thisMenuPermission } = useSelector(state => {
        const { menuPermission } = state?.menu;
        return {
            thisMenuPermission: menuPermission?.find(menu => menu?.path === window.location.pathname)?.permission,
        };
    });

    // component state
    const [tableOptions, setTableOptions] = useState(initialFloorTableOptions);
    const [isShowModal, setIsShowModal] = useState(false);
    const [initialFormValues, setInitialFormValues] = useState(initialFloorValues);

    // fetch data
    const fetchData = useCallback(async query => {
        try {
            const queryParams = {
                page: query?.page + 1 || 1,
                limit: query?.pageSize || 10,
                search: query?.search || "",
                order: query?.orderBy?.field || "floorId",
                direction: query?.orderBy?.field ? query?.orderDirection : "desc",
            };
            const res = await getAllFloor(queryParams, controllerRef.current?.signal);
            if (res) {
                setTableOptions(currentState => ({
                    ...currentState,
                    currentPage: query?.page,
                    pageSize: query?.pageSize || 10,
                    totalData: res?.pagination?.totalData || 0,
                    order: query?.orderBy?.field || "floorId",
                    direction: query?.orderBy?.field ? query?.orderDirection : "desc",
                }));
                return {
                    data: res?.data || [],
                    page: query?.page || 0,
                    totalCount: res?.pagination?.totalData || 0,
                };
            }
            return {
                data: [],
                page: 0,
                totalCount: 0,
            };
        } catch (error) {
            console.dir(error);
            return {
                data: [],
                page: 0,
                totalCount: 0,
            };
        }
    }, []);

    // add floor
    const handleAddFloor = useCallback(async (values, { resetForm }) => {
        try {
            const isCreate = await createFloor(
                {
                    name: values?.floorName,
                    buildingId: values?.buildingId,
                    rights: values?.permission_floor,
                    locationCode: values?.locationCode,
                    locationName: values?.locationName,
                },
                controllerRef.current?.signal,
            );
            if (isCreate) {
                resetForm();
                tableRef.current.onQueryChange();
            }
        } catch (error) {
            console.dir(error);
        }
    }, []);

    // edit floor
    const handleEditFloor = useCallback(async values => {
        try {
            const isUpdate = await updateFloorById(
                values?.floorId,
                {
                    name: values?.floorName,
                    buildingId: values?.buildingId,
                    rights: values?.permission_floor,
                    locationCode: values?.locationCode,
                    locationName: values?.locationName,
                },
                controllerRef.current?.signal,
            );
            if (isUpdate) {
                setIsShowModal(false);
                tableRef.current.onQueryChange();
            }
        } catch (error) {
            console.dir(error);
        }
    }, []);

    // delete floor
    const handleDeleteFloor = useCallback(async floor => {
        try {
            const isConfirm = await swalCondition('<div className="h3 t-text-primary">Are you delete?</div>', "", {
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "YES",
                cancelButtonText: "NO",
            });
            if (isConfirm) {
                const isDelete = await deleteFloorById(floor?.floorId, controllerRef.current?.signal);
                if (isDelete) {
                    tableRef.current.onQueryChange();
                }
            }
        } catch (error) {
            console.dir(error);
        }
    }, []);

    // open edit form modal
    const handleOpenEditFormModal = useCallback(async floor => {
        setInitialFormValues({
            floorId: floor?.floorId,
            permission_floor: floor?.rights,
            buildingId: floor?.buildingId,
            floorName: floor?.name,
            locationCode: floor?.locationCode,
            locationName: floor?.locationName,
        });
        setIsShowModal(true);
    }, []);

    // close modal
    const handleCloseModal = useCallback(async () => {
        setIsShowModal(false);
        setInitialFormValues(initialFloorValues);
    }, []);

    // run only first render
    useEffect(() => {
        controllerRef.current = new AbortController();
        // to avoid memory leaked.
        return () => {
            controllerRef.current.abort();
        };
    }, []);

    return (
        <Grid container spacing={0} className={"py-10"}>
            {/* form add */}
            {thisMenuPermission?.create && (
                <Grid item xs={12} className="mb-10">
                    <FormFloor
                        onSubmit={handleAddFloor}
                        initialFormValues={initialFormValues}
                        isCreateForm
                        buildingOption={buildingOption}
                    />
                </Grid>
            )}

            {/* table */}
            <Grid item xs={12}>
                <div className="t-table-custom">
                    <MaterialTable
                        tableRef={tableRef}
                        components={{ Container: Box }}
                        title=""
                        data={fetchData}
                        options={tableOptions}
                        columns={floorTableColumns}
                        actions={[
                            {
                                icon: "edit",
                                iconProps: { style: { fontSize: 18 }, className: "action-edit" },
                                tooltip: "Edit",
                                onClick: (event, rowData) => handleOpenEditFormModal(rowData),
                                hidden: !thisMenuPermission?.edit,
                            },
                            {
                                icon: "delete",
                                iconProps: { style: { fontSize: 18 }, className: "action-delete" },
                                tooltip: "Delete",
                                onClick: (event, rowData) => handleDeleteFloor(rowData),
                                hidden: !thisMenuPermission?.delete,
                            },
                        ]}
                    />
                </div>
            </Grid>

            {/* modal */}
            <Grid item xs={12}>
                <Modal show={isShowModal} size="md" centered={true} scrollable={true} onHide={handleCloseModal}>
                    <Modal.Header>
                        <Modal.Title>Edit Building</Modal.Title>
                        <IconButton onClick={handleCloseModal} size="small">
                            <Close />
                        </IconButton>
                    </Modal.Header>
                    <ModalBody>
                        <FormFloor
                            onSubmit={handleEditFloor}
                            initialFormValues={initialFormValues}
                            isCreateForm={false}
                            buildingOption={buildingOption}
                        />
                    </ModalBody>
                </Modal>
            </Grid>
        </Grid>
    );
}
